import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Modal, Spinner } from 'react-bootstrap'
import { getContainer } from '../ioc/IOCSetup'
import { RegistrationRequest, RegistrationResponse, Status } from '../http/protocol'
import { Status as AsyncStatus } from '../journey/UserJourneyReducer'
import MetamapComponent from './MetamapComponent'
import { useSelector } from 'react-redux'
import { AppState } from '../RootReducer'
import { t } from '../i18n'
import './kycVerification.scss'
import ModalCloseBtn from '../onboardingpage/modalview/ui/ModalCloseBtn'
import { setModalPath, useClickOutside } from '../onboardingpage/modalview/utils'
import { useHistory } from 'react-router'
import { PATH } from '../onboardingpage/modalview/UserModalController'


enum MetamapProgress {
    NOT_STARTED,
    COMPLETED,
    STARTED,
    EXITED
}

export default function KycVerification() {
    const tempUser = useSelector((app: AppState) => app.user.temp)
    const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>(AsyncStatus.NONE)

    const history = useHistory();

    const [registrationError, setRegistrationError] = useState<ReturnType<typeof handleRegistrationError>>()
    const [registrationResponse, setRegistrationResponse] = useState<RegistrationResponse>()
    const [metamapProgress, setMetamapProgress] = useState<MetamapProgress>(MetamapProgress.NOT_STARTED)

    const handleMetamapCompleted = () => setMetamapProgress(MetamapProgress.COMPLETED)
    const handleMetamapExited = () => setMetamapProgress(MetamapProgress.EXITED)

    const ref = useRef(null);
    useClickOutside(ref, () => setModalPath({ path: undefined, history }));

    const startKyc = () => {
        if (tempUser) {
            setMetamapProgress(MetamapProgress.STARTED)
        } else {
            setRegistrationError("Error Sorry")
            setAsyncStatus(AsyncStatus.ERROR)
        }
    }


    const registerAccount = useCallback(async (request: RegistrationRequest) => {
        setAsyncStatus(AsyncStatus.LOADING)
        try {
            const response = await new Promise<RegistrationResponse>((resolve, reject) => {
                getContainer().getUserService().register(request ?? {})
                    .then(resolve)
                    .catch(reject)
            })

            if (response.status !== Status.SUCCESS) {
                throw new Error("Invalid registration", { cause: response.status })
            }

            setAsyncStatus(AsyncStatus.DONE)
            setRegistrationResponse(response)
        } catch (error) {
            setAsyncStatus(AsyncStatus.ERROR)
            const errorMessage = handleRegistrationError(error);
            setRegistrationError(errorMessage)
            console.error(errorMessage);
        }
    }, [])

    useEffect(() => {
        if (tempUser) {
            registerAccount(tempUser.user)
        }
    }, [])

    useEffect(() => {
        if (metamapProgress == MetamapProgress.COMPLETED) {
            setAsyncStatus(AsyncStatus.LOADING)
            setTimeout(() => {
                setAsyncStatus(AsyncStatus.DONE)
            }, 1000)
        }

    }, [metamapProgress])

    const translations = {
        "kycModal": {
            "title": "KYC Verification",
            "kycCompleted": "You are in a KYC process, this could take a few minutes.",
        }
    }

    return (
        <div className={`modal-wrapper ${PATH.KYC} kyc-verification`} ref={ref}>
            <div className=" modal-container">

                <Modal.Header className='kyc-verification__modal-header'>
                    <ModalCloseBtn onClick={() => setModalPath({ path: undefined, history })} />
                    <h2 className='kyc-verification__modal-header--heading'>{t('metamapAudit.title')}</h2>
                    <p className='kyc-verification__modal-header--paragraph'>{t('metamapAudit.description')}</p>
                </Modal.Header>
                <Modal.Body>
                    <div className='kyc-verification__modal-body'>

                        {asyncStatus == AsyncStatus.DONE && registrationResponse?.userInfo && metamapProgress == MetamapProgress.STARTED && (
                            <div className='kyc-verification__modal-body--metamap'>
                                <MetamapComponent clicked={metamapProgress == MetamapProgress.STARTED} exited={handleMetamapExited} completed={handleMetamapCompleted} user={registrationResponse?.userInfo} />
                            </div>
                        )}

                        {asyncStatus == AsyncStatus.LOADING && (
                            <div className='kyc-verification__modal-body--inprogress'>
                                <Spinner animation={'border'} />
                            </div>
                        )}

                        {asyncStatus == AsyncStatus.DONE && metamapProgress == MetamapProgress.COMPLETED && (
                            <div className='kyc-verification__modal-body--completed'>
                                <p>{t('metamapAudit.verificationSuccess')}</p>
                            </div>
                        )}

                        {asyncStatus == AsyncStatus.DONE && metamapProgress == MetamapProgress.EXITED && (
                            <div className='kyc-verification__modal-body--exited'>
                                <p>Verification cancled, try again. </p>
                                <div className='kyc-verification__modal-body--error'>
                                    <button onClick={startKyc} className='btn btn-cta'>{t('metamapAudit.action')}</button>
                                </div>
                            </div>
                        )}

                        {asyncStatus == AsyncStatus.ERROR && registrationError && (
                            <div className='kyc-verification__modal-body--error'>
                                <p>{registrationError}</p>
                                <button onClick={() => setModalPath({ path: { type: PATH.REGISTRATION, modal: PATH.MODAL_TYPE }, history })} className='btn btn-cta mt-2'>Back to registration</button>
                            </div>
                        )}

                    </div>
                </Modal.Body>
                <Modal.Footer className='kyc-verification__modal-footer'>

                    {metamapProgress == MetamapProgress.NOT_STARTED && asyncStatus == AsyncStatus.DONE && (
                        <div className='kyc-verification__modal-body--error'>
                            <button onClick={startKyc} className='btn btn-cta'>{t('metamapAudit.action')}</button>
                        </div>
                    )}

                    {asyncStatus == AsyncStatus.DONE && metamapProgress == MetamapProgress.COMPLETED && (
                        <div className='kyc-verification__modal-body--completed'>
                            <button onClick={() => setModalPath({ path: { type: PATH.LOGIN, modal: PATH.MODAL_TYPE }, history })} className='btn btn-cta mt-2'>{t('metamapAudit.loginBtn')}</button>
                        </div>
                    )}
                </Modal.Footer>
            </div>

        </div>
    )
}


const handleRegistrationError = (error: any) => {
    if (error.cause) {
        const errorMessages: Record<Status, string> = {
            [Status.SCREEN_NAME_EXIST]: t("user.screenname-taken"),
            [Status.EMAIL_EXIST]: t("user.email-exists"),
            [Status.PHONE_EXIST]: t("user.phone-exists"),
            [Status.INVALID_DATA]: t("user.invalid-registration-details"),
            [Status.INVALID_CHARACTER]: t("user.email-invalid-character"),
            [Status.BLOCKED_DOMAIN]: t("user.email-domain-blocked"),
            [Status.BLOCKED_IP]: t("user.invalid-registration-blockedip"),
            [Status.BLOCKED_COUNTRY]: t("user.invalid-registration-blockedcountry"),
            [Status.PHONE_INVALID_FORMAT]: t("forms.invalid-phone-number"),
            [Status.UNDER_AGE]: t("forms.under-age"),
            [Status.DENIED]: t("forms.denied"),
            [Status.INVALID_PROMO_CODE]: t("user.invalid-promo-code"),
            [Status.SUCCESS]: "", // No error message needed for success
        };

        return errorMessages[error.cause as Status] || t("user.invalid-registration-details");
    }
    return t("user.unknown-error");
};

